import React from 'react';
import { inject, observer } from 'mobx-react';
import {
  Alert,
  Button,
  ButtonToolbar,
  Card,
  Col,
  FormCheck,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';
import moment from 'moment';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import Computation from '../../models/computations/Computation';
import { PROFILES_PERMISSIONS } from '../../permissions';
import Store from '../../store';
import {
  computationDisplayName,
  isComputationDeployable,
  isUsedInTerritory,
} from '../../utils';
import checkPermissions from '../components/CheckPermissions';
import Error from '../components/error/Error';
import Spinner from '../components/Spinner';
import Status from '../components/computations/Status';
import PageHeader from '../components/PageHeader';
import Table from '../components/generic_components/Table';

type ComputationsPageProps = RouteComponentProps & {
  store?: Store;
};

const ComputationsPage = inject('store')(
  observer(
    class ComputationsPage extends React.Component<ComputationsPageProps> {
      constructor(props: ComputationsPageProps) {
        super(props);

        this.saveComputationsArgo = this.saveComputationsArgo.bind(this);
        this.confirmSaveComputationsArgo =
          this.confirmSaveComputationsArgo.bind(this);
        this.toggleSelectedForDeploy = this.toggleSelectedForDeploy.bind(this);
        this.cancelChanges = this.cancelChanges.bind(this);
        this.confirmCancelChanges = this.confirmCancelChanges.bind(this);
        this.hideAllModals = this.hideAllModals.bind(this);
      }

      async componentDidMount() {
        this.props.store.computationsPage.loading = true;
        this.props.store.computationsPage.savingError = null;
        this.props.store.computationsPage.message = null;
        this.props.store.computationsPage.selectedForDeploy = [];
        await this.props.store.getComputations();
        await this.props.store.getMetadataFieldsForComputations();
        if (this.props.store.computationsPage.computations.length === 0) {
          await this.props.store.initComputations();
          await this.props.store.getComputations();
        }

        this.props.store.computationsPage.loading = false;
      }

      saveComputationsArgo(options: {
        withRecompute: boolean;
        selectedOnly?: boolean;
      }) {
        this.props.store.computationsPage.savingError = null;
        this.props.store.computationsPage.modalConfirmSave = {
          active: true,
          withRecompute: options.withRecompute,
          selectedOnly: options.selectedOnly ?? false,
        };
        this.props.store.computationsPage.message = null;
      }

      toggleSelectedForDeploy(name: string) {
        const { computationsPage } = this.props.store;
        computationsPage.selectedForDeploy =
          computationsPage.selectedForDeploy.includes(name)
            ? computationsPage.selectedForDeploy.filter((it) => it !== name)
            : [...computationsPage.selectedForDeploy, name];
      }

      async confirmSaveComputationsArgo() {
        const { computationsPage } = this.props.store;
        computationsPage.saving = true;
        try {
          if (computationsPage.modalConfirmSave.selectedOnly) {
            await this.props.store.deployComputations(
              computationsPage.selectedForDeploy.slice()
            );
          } else {
            await this.props.store.saveComputationsArgo();
          }
          computationsPage.selectedForDeploy = [];

          this.props.store.computationsPage.loading = true;
          await this.props.store.getComputations();
          this.props.store.computationsPage.loading = false;
          this.props.store.computationsPage.message =
            'Released! Computations will be available in the next dataset you will load.';
        } catch (e) {
          // The Error banner reads savingError.message, so assigning the string
          // rendered nothing at all - a failed deploy was silent. A transport
          // failure has no `.response` either, and reaching through it threw
          // inside the catch, leaving the modal stuck on "Deploying...".
          this.props.store.computationsPage.savingError = e?.response?.body ?? {
            message:
              e?.message ??
              'The deploy failed before studio answered. Check the studio logs for this tenant.',
            type: 'other',
            status: e?.status ?? 500,
          };
          window.scrollTo(0, 0);
        }
        this.props.store.computationsPage.saving = false;
        this.hideAllModals();
      }

      cancelChanges() {
        this.props.store.computationsPage.savingError = null;
        this.props.store.computationsPage.modalConfirmCancelChanges = true;
        this.props.store.computationsPage.message = null;
      }

      async confirmCancelChanges() {
        await this.props.store.deleteComputationsCache();
        this.props.store.computationsPage.selectedForDeploy = [];
        this.hideAllModals();
        this.props.store.computationsPage.loading = true;
        await this.props.store.getComputations();
        this.props.store.computationsPage.loading = false;
        this.props.store.computationsPage.message = 'Changes cancelled!';
        window.scrollTo(0, 0);
      }

      hideAllModals() {
        this.props.store.computationsPage.modalConfirmSave = {
          active: false,
          withRecompute: false,
          selectedOnly: false,
        };
        this.props.store.computationsPage.modalConfirmCancelChanges = false;
      }

      render() {
        const { store } = this.props;
        const { computationsPage, tenant } = store;
        const {
          computations,
          computationsInProgress,
          loading,
          loadingError,
          saving,
          savingError,
          modalConfirmSave,
          modalConfirmCancelChanges,
          message,
          selectedForDeploy,
          selectiveDeployEnabled,
        } = computationsPage;

        const columnHelper = createColumnHelper<Computation>();

        const selectColumn: ColumnDef<Computation, unknown> = {
          id: 'select',
          header: '',
          // Explicit null on the empty branch. flexRender treats this function as
          // a component, so anything other than an element or null/false throws.
          cell: ({ row }) =>
            isComputationDeployable(row.original) ? (
              <FormCheck
                type="checkbox"
                aria-label={`Select ${computationDisplayName(
                  row.original.name
                )} for deployment`}
                checked={selectedForDeploy.includes(row.original.name)}
                onChange={() => this.toggleSelectedForDeploy(row.original.name)}
              />
            ) : null,
          enableSorting: false,
          enableColumnFilter: false,
        };

        const columns: ColumnDef<Computation, unknown>[] = [
          ...(selectiveDeployEnabled ? [selectColumn] : []),
          {
            accessorFn: ({ name }) => computationDisplayName(name),
            id: 'label',
            header: 'Label',
            cell: (info) => (
              <>
                {info.row.original.context === 'standard' && (
                  <OverlayTrigger
                    overlay={
                      <Tooltip id="standard-tooltip">
                        Standard computation
                      </Tooltip>
                    }
                  >
                    <i aria-hidden className="fas fa-lock me-2" />
                  </OverlayTrigger>
                )}
                <a
                  href={`/tenant/${tenant}/computations/${info.row.original.name}`}
                >
                  {info.getValue()}
                </a>
              </>
            ),
            sortDescFirst: false,
          },
          {
            accessorFn: (computation): string => {
              const statuses = [];

              if (isUsedInTerritory(computation)) statuses.push('territory');
              if (computation.usedInLiveModel) statuses.push('live');
              if (computation.isSynced) statuses.push('sync');

              return statuses.join(' and ') || '-';
            },
            id: 'status',
            header: 'Status',
            cell: (info) => {
              const value = info.getValue() as string;

              return (
                <>
                  {value.includes('territory') && (
                    <Status
                      text="Territory"
                      title="Used in territory assignments"
                      background="warning"
                    />
                  )}
                  {value.includes('live') && (
                    <Status
                      text="Live"
                      title="Used in a live customer fit model"
                      background="success"
                    />
                  )}
                  {value.includes('sync') && (
                    <Status
                      text="Sync"
                      title="Synced to your CRM directly"
                      background="primary"
                    />
                  )}
                </>
              );
            },
            sortingFn: (rowA, rowB) => {
              const getScore = (computation: Computation) => {
                let score = 0;

                if (isUsedInTerritory(computation)) score += 8;
                if (computation.usedInLiveModel) score += 4;
                if (computation.isSynced) score += 2;
                if (computation.isDraft) score += 1;

                return score;
              };

              const scoreA = getScore(rowA.original);
              const scoreB = getScore(rowB.original);

              return scoreA - scoreB;
            },
            filterFn: ({ original }, _, filterValue) => {
              if (filterValue === 'territory')
                return isUsedInTerritory(original);
              if (filterValue === 'live') return original.usedInLiveModel;
              if (filterValue === 'sync') return original.isSynced;
              if (filterValue === 'live and sync')
                return original.usedInLiveModel && original.isSynced;

              return (
                !original.usedInLiveModel &&
                !original.isSynced &&
                !isUsedInTerritory(original)
              );
            },
          },
          columnHelper.accessor('type', {
            // ?? null for the same reason as the select cell above: flexRender
            // treats this as a component, and undefined is React error #152.
            cell: (info) => (info.getValue() as string) ?? null,
            header: 'Type',
          }),
          {
            accessorFn: ({ updatedAt, context }) =>
              context === 'standard' ? '-' : moment(updatedAt).format('ll'),
            id: 'updatedAt',
            header: 'Updated At',
            cell: (info) => info.getValue(),
            sortingFn: (rowA, rowB) => {
              if (
                rowA.original.context === 'standard' ||
                rowB.original.context === 'standard'
              ) {
                if (rowA.original.context === rowB.original.context) {
                  return 0;
                } else if (rowA.original.context === 'standard') {
                  return -1;
                } else {
                  return 1;
                }
              }

              return moment(rowA.original.updatedAt).diff(
                moment(rowB.original.updatedAt)
              );
            },
            sortDescFirst: true,
            enableColumnFilter: false,
          },
        ];

        return (
          <>
            <PageHeader>
              <Row>
                <Col>
                  <h2
                    style={{ fontWeight: 700, margin: 0, marginBottom: '4px' }}
                    id="page-header-computations"
                  >
                    Computations
                  </h2>
                  <p
                    style={{
                      fontSize: '14px',
                      color: '#6B7280',
                      margin: 0,
                      fontWeight: 400,
                    }}
                  >
                    Manage your data computations and transformations.
                  </p>
                </Col>
                <Col className="text-end">
                  {checkPermissions(
                    PROFILES_PERMISSIONS.ARCHITECT,
                    <LinkContainer to={`/tenant/${tenant}/computations/new`}>
                      <Button className="ms-3" id="add-computation-button">
                        <i className="fas fa-plus" /> Add computation
                      </Button>
                    </LinkContainer>
                  )}
                </Col>
              </Row>
            </PageHeader>

            <div className="mt-3 mb-5">
              <Card border="light">
                <Card.Body>
                  <p>
                    Computations store information about a Person, a Company or
                    the combination of both. Your HG Insights account includes
                    computations by default. Build new computations to enrich
                    your models or segment your leads and accounts directly in
                    your CRM.
                  </p>
                  <a
                    href="https://help.madkudu.com/docs/what-are-computations"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none text-primary"
                  >
                    <i aria-hidden className="fas fa-book-open"></i> How
                    computations work
                  </a>
                  &nbsp; &nbsp;
                  <span>
                    <a
                      href="https://help.madkudu.com/docs/definitions-of-standard-computations"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none text-primary"
                    >
                      <i aria-hidden className="fas fa-book-open"></i>{' '}
                      Definition of Standard Computations
                    </a>
                  </span>
                </Card.Body>
              </Card>
              {(loadingError || savingError) && (
                <Error
                  message={loadingError || savingError?.message}
                  type={savingError?.type}
                />
              )}
              {loading ? (
                <Spinner />
              ) : (
                <>
                  {computationsInProgress && (
                    <Alert className="d-flex mt-3" variant="info">
                      You have new computations or changes not deployed yet.
                      {checkPermissions(
                        PROFILES_PERMISSIONS.ARCHITECT,
                        <ButtonToolbar className="ms-auto">
                          {selectiveDeployEnabled && (
                            <Button
                              className="me-2"
                              variant="outline-primary"
                              disabled={selectedForDeploy.length === 0}
                              onClick={() =>
                                this.saveComputationsArgo({
                                  withRecompute: false,
                                  selectedOnly: true,
                                })
                              }
                            >
                              Deploy selected ({selectedForDeploy.length})
                            </Button>
                          )}
                          <Button
                            className="me-2"
                            disabled={
                              computations.filter(
                                ({ name }) => name.trim().length === 0
                              ).length > 0
                            }
                            onClick={() =>
                              this.saveComputationsArgo({
                                withRecompute: false,
                              })
                            }
                          >
                            {selectiveDeployEnabled ? 'Deploy all' : 'Deploy'}
                          </Button>
                          <Button variant="danger" onClick={this.cancelChanges}>
                            Cancel changes
                          </Button>
                        </ButtonToolbar>
                      )}
                    </Alert>
                  )}
                  {message && (
                    <Alert className="mt-3" variant="success">
                      <div dangerouslySetInnerHTML={{ __html: message }} />
                    </Alert>
                  )}
                  <Card className="mt-3 h-75">
                    <Card.Body>
                      <Table
                        data={computations}
                        columns={columns}
                        defaultSorting={[{ id: 'updatedAt', desc: true }]}
                        defaultPageSize={50}
                      />
                    </Card.Body>
                  </Card>
                  <Modal
                    show={modalConfirmSave.active}
                    onHide={this.hideAllModals}
                  >
                    <Modal.Header>
                      <Modal.Title>
                        Deploy{' '}
                        {modalConfirmSave.selectedOnly
                          ? `${selectedForDeploy.length} computation(s)`
                          : 'computations'}{' '}
                        in production{' '}
                        {modalConfirmSave.withRecompute &&
                          'and reload datasets'}
                        ?
                      </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      {modalConfirmSave.selectedOnly ? (
                        <p>
                          Only these computations will be deployed:
                          <ul>
                            {selectedForDeploy.map((name) => (
                              <li key={name}>{computationDisplayName(name)}</li>
                            ))}
                          </ul>
                          Every other change stays pending, including deleted
                          computations — those are only removed by{' '}
                          <b>Deploy all</b>. Updates made on computations marked{' '}
                          <b>Live</b> may impact the model(s) using them, and
                          will be available at the <b>next batch update</b>.
                        </p>
                      ) : modalConfirmSave.withRecompute ? (
                        <p>
                          This will:
                          <ul>
                            <li>
                              deploy any new computation or updates made to
                              computations.
                            </li>
                            <li>
                              start reloading the training and validation
                              datasets with these updates.{' '}
                              <b>
                                It may take several hours where the model will
                                not be accessible for this duration.
                              </b>
                            </li>
                          </ul>
                          Updates made on computations marked <b>Live</b> may
                          impact the model(s) using them. The updates will be
                          available at the next batch update.
                        </p>
                      ) : (
                        <p>
                          This will deploy any new computation or updates made
                          to computations. Updates made on computations marked{' '}
                          <b>Live</b> may impact the model(s) using them. The
                          updates will be available at the{' '}
                          <b>next batch update</b>.
                        </p>
                      )}
                    </Modal.Body>
                    <Modal.Footer>
                      <Button
                        variant="primary"
                        onClick={this.confirmSaveComputationsArgo}
                      >
                        {saving ? 'Deploying...' : 'Deploy'}
                      </Button>
                      <Button
                        onClick={this.hideAllModals}
                        disabled={saving}
                        variant="outline-primary"
                      >
                        Cancel
                      </Button>
                    </Modal.Footer>
                  </Modal>
                  <Modal
                    show={modalConfirmCancelChanges}
                    onHide={this.hideAllModals}
                  >
                    <Modal.Header>
                      <Modal.Title>Confirmation</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <p>
                        Are you sure you want to cancel the current changes? All
                        your changes not released will be lost.
                      </p>
                    </Modal.Body>
                    <Modal.Footer>
                      <Button
                        variant="primary"
                        onClick={this.confirmCancelChanges}
                      >
                        Cancel changes
                      </Button>
                      <Button
                        onClick={this.hideAllModals}
                        variant="outline-primary"
                      >
                        Cancel
                      </Button>
                    </Modal.Footer>
                  </Modal>
                </>
              )}
            </div>
          </>
        );
      }
    }
  )
);

export default withRouter(ComputationsPage);
